import { accordionHeight, gapBetweenItems } from 'components/Accordion';
import { cardHeight, cardMargin } from 'components/WorkOrderCard/styles';
import { WorkOrderItemProps } from 'components/WorkOrderItem/WorkOrderItem';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';

type Workorder = WorkOrderItemProps['workorder'];

export interface Group {
  label: string;
  items: Workorder[];
  isDefaultExpanded: boolean;
}

/**
 * The list is flattened into fixed height rows so it can be virtualized. A row
 * is either a group header, a single row of the card grid, or a leading or
 * trailing spacer. Every height is known up front, so nothing has to be
 * measured.
 */
export type Row =
  | { type: 'header'; group: Group }
  | { type: 'cards'; items: Workorder[] }
  | { type: 'spacer'; height: number };

/**
 * Inset above the first drawer and below the last, matching Menu's
 * `--padding-top` / `--padding-bottom`, and symmetrical with each other. This
 * has to live inside the scrolled content as a row, not as padding on the
 * container `AutoSizer` measures — `IonContent` positions its scrollable
 * element with its own top offset math, and padding on the measured element
 * fights that, which is what was clipping the first row.
 */
const edgeSpacerHeight = 12;

/**
 * `IonCol` defaults to 5px of padding. It is set explicitly here — and
 * exported for the card grid's own `IonCol` to reuse — so the row height
 * below stays correct even if that default changes.
 */
export const columnPadding = 5;
const cardRowHeight = cardHeight + cardMargin * 2 + columnPadding * 2;

/**
 * Ionic's `sizeXl` breakpoint (1200px) is the largest one it ships, but the
 * persistent side menu (see Menu's `--width`) eats into content width above
 * that point, so 3 columns need more room than Ionic's own breakpoint gives
 * them. This overrides `sizeXl`'s effect until the viewport is wide enough to
 * actually fit a third column comfortably. Exported so the page's own
 * `cardColumn` style can override the same breakpoint.
 */
export const threeColumnBreakpoint = 1366;

const getRowHeight = (row: Row) => {
  switch (row.type) {
    case 'header':
      return accordionHeight;
    case 'spacer':
      return row.height;
    default:
      return cardRowHeight;
  }
};

/**
 * Mirrors the breakpoints of the page's `IonCol` sizes. The column count has
 * to be known in JS as well as CSS because items are chunked into rows before
 * they reach the virtualized list.
 */
const getColumnCount = () => {
  if (window.matchMedia(`(min-width: ${threeColumnBreakpoint}px)`).matches) return 3;
  if (window.matchMedia('(min-width: 768px)').matches) return 2;
  return 1;
};

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Flattens grouped work orders into virtualized rows, and owns everything
 * `VariableSizeList` needs to render them correctly: tracking which groups
 * are expanded, recomputing the responsive column count on resize, and
 * invalidating the list's cached row offsets whenever a toggle or breakpoint
 * change reshapes the rows.
 */
export const useVirtualizedList = (groups: Group[]) => {
  const listRef = useRef<List>(null);

  const [columnCount, setColumnCount] = useState(getColumnCount);

  useEffect(() => {
    const handleResize = () => setColumnCount(getColumnCount());

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Only groups the user has toggled are tracked. Anything absent falls back
   * to its default, so groups that appear once work orders load still open or
   * close as intended.
   */
  const [toggledGroups, setToggledGroups] = useState<{ [label: string]: boolean }>({});

  const isExpanded = (group: Group) => toggledGroups[group.label] ?? group.isDefaultExpanded;

  const toggleGroup = (group: Group) =>
    setToggledGroups((prev) => ({
      ...prev,
      [group.label]: !(prev[group.label] ?? group.isDefaultExpanded),
    }));

  const rows = useMemo(() => {
    const next = groups.reduce<Row[]>((acc, group, index) => {
      acc.push({ type: 'header', group });

      if (isExpanded(group)) {
        const cardRows = chunk(group.items, columnCount);
        cardRows.forEach((items) => acc.push({ type: 'cards', items }));

        // Matches the accordion's own trailing gap, so a group's cards sit as
        // far from the next drawer header as the header sits from its cards.
        if (cardRows.length && index < groups.length - 1) {
          acc.push({ type: 'spacer', height: gapBetweenItems });
        }
      }

      return acc;
    }, []);

    if (next.length) {
      next.unshift({ type: 'spacer', height: edgeSpacerHeight });
      next.push({ type: 'spacer', height: edgeSpacerHeight });
    }

    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, toggledGroups, columnCount]);

  /**
   * `VariableSizeList` caches row offsets, so they have to be invalidated
   * whenever expanding, collapsing or a breakpoint change reshapes the list.
   */
  useLayoutEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [rows]);

  return {
    listRef,
    rows,
    isExpanded,
    toggleGroup,
    itemSize: (index: number) => getRowHeight(rows[index]),
  };
};
